import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { RecordAudioExerciseContent } from "content";
import { useIsClassroom } from "hooks/common/useIsClassroom/useIsClassroom";
import { useRole } from "hooks/common";
import { useLessonSettings } from "hooks/redux";
import {
  useUpdateChapterStudentProgress,
  useUpdateHomeworkStudentProgress,
  useUploadAudio,
} from "hooks/api/student-progress";
import { ExerciseHeader } from "components/Common/ExerciseHeader";
import { Space } from "components/Common/Space";
import { Paragraph } from "components/Typography/Paragraph";
import { Button } from "components/Interactive/Button";
import { TRecordAudioExercisePayload } from "types/app/exercises";
import { TRecordAudioProps, TRecordingState } from "./RecordAudio.types";
import "./RecordAudio.styles.scss";

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export const RecordAudio: React.FC<TRecordAudioProps> = (props) => {
  const { chapterId, exercise, prefix = null, isHomeworkExercise } = props;

  const { courseId, lessonId, studentId } = useParams();
  const { isClassroom } = useIsClassroom();
  const { isUserStudent } = useRole();
  const { isExerciseReadOnlyMode } = useLessonSettings();

  const [recordingState, setRecordingState] = useState<TRecordingState>("idle");
  const [localBlobUrl, setLocalBlobUrl] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const audioReference = exercise.payload.data.audioReference;
  const maxDurationSeconds = exercise.payload.data.maxDurationSeconds;

  const { mutate: uploadAudio } = useUploadAudio();
  const { mutate: updateChapterProgress } =
    useUpdateChapterStudentProgress<TRecordAudioExercisePayload>(
      courseId,
      lessonId,
      chapterId,
      studentId
    );
  const { mutate: updateHomeworkProgress } =
    useUpdateHomeworkStudentProgress<TRecordAudioExercisePayload>(
      courseId,
      lessonId,
      chapterId,
      studentId
    );

  useEffect(() => {
    if (audioReference && recordingState === "idle") {
      setRecordingState("confirmed");
    }
  }, [audioReference]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        audioBlobRef.current = blob;
        if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
        const url = URL.createObjectURL(blob);
        setLocalBlobUrl(url);
        setRecordingState("recorded");
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start();
      setElapsedSeconds(0);
      setRecordingState("recording");
      timerRef.current = setInterval(
        () => setElapsedSeconds((s) => s + 1),
        1000
      );
    } catch {
      // user denied microphone or not available
    }
  }, [localBlobUrl]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    mediaRecorderRef.current?.stop();
  }, []);

  useEffect(() => {
    if (
      recordingState === "recording" &&
      elapsedSeconds >= maxDurationSeconds
    ) {
      stopRecording();
    }
  }, [elapsedSeconds, recordingState, maxDurationSeconds, stopRecording]);

  const reRecord = useCallback(() => {
    if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    setLocalBlobUrl(null);
    audioBlobRef.current = null;
    setRecordingState("idle");
  }, [localBlobUrl]);

  const confirmRecording = useCallback(() => {
    if (!audioBlobRef.current) return;
    setRecordingState("uploading");

    uploadAudio(audioBlobRef.current, {
      onSuccess: (reference) => {
        const updated = {
          ...exercise,
          payload: {
            ...exercise.payload,
            data: { ...exercise.payload.data, audioReference: reference },
          },
        };
        const onSuccess = () => setRecordingState("confirmed");
        const onError = () => setRecordingState("recorded");
        if (isHomeworkExercise) {
          updateHomeworkProgress(updated, { onSuccess, onError });
        } else {
          updateChapterProgress(updated, { onSuccess, onError });
        }
      },
      onError: () => setRecordingState("recorded"),
    });
  }, [
    exercise,
    isHomeworkExercise,
    uploadAudio,
    updateChapterProgress,
    updateHomeworkProgress,
  ]);

  const canRecord = isUserStudent && isClassroom && !isExerciseReadOnlyMode;
  const activeAudioUrl = localBlobUrl ?? audioReference;

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      <ExerciseHeader<TRecordAudioExercisePayload>
        chapterId={chapterId}
        exercise={exercise}
        prefix={prefix}
      />
      <Paragraph variant={2} lineBreak>
        {exercise.payload.data.description}
      </Paragraph>

      {!canRecord ? (
        activeAudioUrl ? (
          <audio
            className="nb-exercise-record-audio__player"
            src={activeAudioUrl}
            controls
          />
        ) : (
          <Paragraph
            variant={2}
            className="nb-exercise-record-audio__no-recording"
          >
            {RecordAudioExerciseContent.Main.Status.NO_RECORDING}
          </Paragraph>
        )
      ) : (
        <div className="nb-exercise-record-audio__controls">
          {recordingState === "idle" && (
            <Space direction="vertical" size="small" justify="start">
              <span className="nb-exercise-record-audio__limit">
                {RecordAudioExerciseContent.Main.Label.MAX_DURATION(
                  maxDurationSeconds
                )}
              </span>
              <Button variant="primary" onClick={startRecording}>
                {RecordAudioExerciseContent.Main.Button.RECORD}
              </Button>
            </Space>
          )}

          {recordingState === "recording" && (
            <>
              <span className="nb-exercise-record-audio__status nb-exercise-record-audio__status--recording">
                {RecordAudioExerciseContent.Main.Status.RECORDING}
              </span>
              <span className="nb-exercise-record-audio__timer">
                {formatTime(maxDurationSeconds - elapsedSeconds)}
              </span>
              <Button variant="secondary" onClick={stopRecording}>
                {RecordAudioExerciseContent.Main.Button.STOP}
              </Button>
            </>
          )}

          {(recordingState === "recorded" || recordingState === "uploading") &&
            localBlobUrl && (
              <Space
                direction="vertical"
                size="small"
                justify="start"
                fullWidth
              >
                <audio
                  className="nb-exercise-record-audio__player"
                  src={localBlobUrl}
                  controls
                />
                <Space direction="horizontal" size="small" justify="start">
                  <Button
                    variant="secondary"
                    onClick={reRecord}
                    disabled={recordingState === "uploading"}
                  >
                    {RecordAudioExerciseContent.Main.Button.RE_RECORD}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={confirmRecording}
                    disabled={recordingState === "uploading"}
                  >
                    {recordingState === "uploading"
                      ? RecordAudioExerciseContent.Main.Status.UPLOADING
                      : RecordAudioExerciseContent.Main.Button.CONFIRM}
                  </Button>
                </Space>
              </Space>
            )}

          {recordingState === "confirmed" && (
            <Space direction="vertical" size="small" justify="start" fullWidth>
              {activeAudioUrl && (
                <audio
                  className="nb-exercise-record-audio__player"
                  src={activeAudioUrl}
                  controls
                />
              )}
              <Button variant="secondary" onClick={reRecord}>
                {RecordAudioExerciseContent.Main.Button.RE_RECORD}
              </Button>
            </Space>
          )}
        </div>
      )}
    </Space>
  );
};
